import { gl } from '../../Engine';
import { RenderStorage } from '../../core/render/RenderStorage';
import { AdjacentSliceGhostRenderer } from '../../core/render/renderers/slices/AdjacentSliceGhostRenderer';
import {
    ContourRenderer,
    type ContourRenderOptions,
} from '../../core/render/renderers/ContourRenderer';
import { Environment } from '../../data/Environment';
import { StructureSetObject } from '../../data/structure/StructureSetObject';
import { TextureView } from '../TextureView';
import { PlaneView } from '../views/PlaneView';
import { Layer } from './Layer';
import {
    isSelectedStructurePeeked,
    resolveContourRenderOptions,
} from './contourAlpha';
import { LayerBit } from './LayerBit';

/**
 * {@link ContourLayer} provides common properties of {@link Layer Layers} for {@link StructureSetObject Contour}data.\
 * {@link ContourLayer ContourLayers} have {@link internalFormat} of type {@link gl.RGBA8} and\
 * {@link format} of type {@link gl.RGBA}.
 */
export class ContourLayer extends Layer<StructureSetObject> {
    /**
     * {@link ContourLayer} constructor.
     * @param parent {@link TextureView} on which this {@link ContourLayer} resides.
     */
    public constructor(parent: TextureView, env: Environment) {
        super(parent, env);
    }

    /**
     * The internal format of {@link ContourLayer}s' {@link Layer._texture texture}.\
     * {@link ContourLayer ContourLayers} use this format to support colors.\
     * The number of {@link TextureView Views} & {@link Layer Layers} is so small
     * that the memory difference between 8bit and 32bit view textures does not make much difference.
     */
    public get internalFormat(): number {
        return gl.RGBA8;
    }

    /**
     * The format of the {@link Layer._texture texture} this {@link Layer} defines.
     */
    public get format(): number {
        return gl.RGBA;
    }

    /**
     * The {@link LayerBit.CONTOUR_LAYER_BIT}
     */
    public get layerBit(): number {
        return LayerBit.CONTOUR_LAYER_BIT;
    }

    /**
     * Method to retrieve the {@link StructureSetObject StructureSetObjects} currently loaded in the {@link Environment}.
     * @param environment The {@link Environment} from which the {@link StructureSetObject StructureSetObjects} are loaded.
     * @returns An {@link Array} of all the {@link StructureSetObject StructureSetObjects} currently loaded in the {@link environment}.
     */
    public getTargets(environment: Environment): StructureSetObject[] {
        if (environment.structureSet) {
            return [environment.structureSet];
        }
        return [];
    }

    /**
     * Method to draw the given structure sets on this {@link Layer Layer's} texture.
     * @param structures The {@link StructureSetObject StructureSetObjects} that are to be rendered.
     */
    protected draw(structures: StructureSetObject[]): void {
        this.bindAndClearTexture();
        const contourRenderer = RenderStorage.getRenderer(ContourRenderer);
        // Preview and isolate reduce to one alpha; precedence lives in
        // resolveContourRenderOptions.
        const preview = this.env.operationPreviewStructure;
        const dimming = resolveContourRenderOptions({
            previewActive: !!preview,
            previewDimsOtherStructures: !!preview?.dimsOtherStructures,
            isolationActive: this.env.structureIsolationActive,
        });
        // Independent of the alpha: a RESULT preview removes the previewed
        // structure from this layer, because the overlay drawn after it
        // already shows what that structure becomes, and a slice the
        // operation empties can only read as empty if nothing is drawn on
        // it. Dimming cannot achieve that — the renderer exempts the
        // selected structure from the dim and draws it at double thickness.
        let options: ContourRenderOptions | undefined =
            preview?.hidesPreviewedStructure
                ? { ...dimming, hideSelected: true }
                : dimming;
        // A peek withholds the selected structure without anything taking
        // its place, so it is named beside the dimming instead of through
        // `hideSelected`: the other contours keep the alpha they had before
        // it. Named only while one is on, so an idle layer asks for a plain
        // pass.
        const peekedRoiNumber = this.env.peekedRoiNumber;
        if (peekedRoiNumber !== null) {
            options = { ...options, peekedRoiNumber };
        }
        // Ghosts go under the contours, so where one coincides with the
        // displayed slice's contour the user sees the continuous line. They
        // are the selected structure's own outline on the neighbouring
        // slices and lie along the boundary a peek uncovers, so they are
        // withheld with its contour.
        const selectedStructurePeeked = isSelectedStructurePeeked(
            this.env.structureSet?.selectedStructure?.roiNumber,
            peekedRoiNumber
        );
        if (!options?.hideSelected && !selectedStructurePeeked) {
            try {
                this.drawAdjacentSliceGhosts();
            } catch {
                console.warn('Adjacent slice ghosts could not be drawn.');
            }
        }
        contourRenderer.render(this.getView(), structures, options);
    }

    /**
     * Draw the selected structure's ghosts for the slices the
     * {@link Environment} names, while the aid is on; a structure the contour
     * pass would not draw ghosts nothing. The texture is resolved first, as
     * lazy SDF generation switches the GL program.
     */
    private drawAdjacentSliceGhosts(): void {
        const depths = this.env.adjacentSliceGhosts;
        const view = this.getView();
        if (!depths || !(view instanceof PlaneView)) {
            return;
        }
        const roi = this.env.structureSet?.selectedStructure;
        const texture = roi?.drawableTexture;
        if (!roi || !texture) {
            return;
        }
        RenderStorage.getRenderer(AdjacentSliceGhostRenderer).render(
            view,
            texture,
            roi.color,
            roi.contourThickness,
            depths
        );
    }
}
